'use client'
import React, { useCallback, useEffect, useState } from 'react'
import type { AreaItem } from '@/types/cms/AreaItem'
import type { Context } from '@/types/app'
import { useRouter } from 'next/navigation'
import { getEventService } from '@/services/events/EventInstance'
import Icon from '@/services/components/strategies/common/Icon'
import NavigationViewDrawer from './NavigationViewDrawer'
import type { ServerSection } from './NavigationViewDrawerTrigger.server'

type NavBase = Omit<AreaItem, 'children'> & { url?: string }
type NavItem = NavBase & { children?: NavItem[] } // island does not need __key

export type HistoryItem = {
  title: string
  items: NavItem[]
  key: string
}

export default function DrawerIsland({
  nestedItems,
  context,
  sections,
}: {
  nestedItems: { children: NavItem[] }
  context: Context
  sections: ServerSection[]
}) {
  const router = useRouter()
  const [isOpen, setIsOpen] = useState(false)
  const [currentItems, setCurrentItems] = useState<NavItem[]>(
    nestedItems.children,
  )
  const [currentTitle, setCurrentTitle] = useState('')
  const [history, setHistory] = useState<HistoryItem[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [currentKey, setCurrentKey] = useState('root')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    if (isOpen) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen])

  const handleNavigate = useCallback(
    (item: NavItem) => {
      // find index of the item at the current level (by id)
      const idx = currentItems.findIndex((n) => n.id === item.id)

      if (item.children?.length) {
        setHistory((prev) => [
          ...prev,
          { title: currentTitle, items: currentItems, key: currentKey },
        ])
        setCurrentTitle(item.title ?? '')
        setCurrentItems(item.children)
        setSelectedIndex(0)

        // derive the same key scheme the server used: root / idx / idx2 / ...
        const nextKey = idx >= 0 ? `${currentKey}/${idx}` : `${currentKey}` // fallback just in case
        setCurrentKey(nextKey)
      } else {
        router.push(item.url ?? '/')
        setCurrentItems(nestedItems.children)
        setCurrentTitle('')
        setHistory([])
        setSelectedIndex(0)
        setCurrentKey('root')
        setIsOpen(false)
      }
    },
    [currentItems, currentKey, currentTitle, nestedItems.children, router],
  )

  useEffect(() => {
    const es = getEventService()
    const onNav = (event: CustomEvent) => handleNavigate(event.detail)
    es.on('navigate-menu', onNav)
    return () => es.off('navigate-menu', onNav)
  }, [handleNavigate])

  const handleGoBack = () => {
    setHistory((prev) => {
      if (!prev.length) return prev
      const next = [...prev]
      const last = next.pop()!
      setCurrentItems(last.items)
      setCurrentTitle(last.title || '')
      setSelectedIndex(0)
      setCurrentKey(last.key)
      return next
    })
  }

  return (
    <>
      {/* trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex h-8 w-8 items-center justify-center"
        aria-label="Menü öffnen"
        aria-expanded={isOpen}
        aria-controls="site-drawer"
      >
        <Icon name="hamburger" size={24} />
        <span className="sr-only">Menü öffnen</span>
      </button>

      {/* overlay */}
      <NavigationViewDrawer
        nestedItems={currentItems as unknown as AreaItem[]}
        context={context}
        currentTitle={currentTitle}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        selectedIndex={selectedIndex}
        history={history}
        handleGoBack={handleGoBack}
        sections={sections}
        currentKey={currentKey}
      />
    </>
  )
}
